import React, { createContext, useContext, useEffect, useState, ReactNode } from "react";

type Language = 'en' | 'es';

interface Translations {
    [key: string]: {
        [key: string]: string;
    };
}

const translations: Translations = {
    en: {
        // Nav
        "nav.home": "Home",
        "nav.skills": "Skills",
        "nav.services": "Services",
        "nav.portfolio": "Work",
        "nav.contact": "Hire Me",
        // Hero
        "home.hi": "I Build Software That Moves Money & Scales Businesses",
        "home.role": "Senior Product Engineer · Fintech & Marketplaces",
        "home.description": "7 years designing backend systems, cross-platform apps, and automated payment workflows for banking, fintech, and e-commerce. Founder of Fabricantes Digital SpA. I don't just write code — I architect solutions that generate revenue.",
        "home.letsTalk": "Let's Solve Your Problem →",
        "home.badge1": "Banking & Fintech Systems",
        "home.badge2": "Cross-border Payments",
        "home.badge3": "Marketplace Architecture",
        // Stats
        "stats.years": "Years",
        "stats.yearsLabel": "in Banking & Fintech",
        "stats.countries": "Countries",
        "stats.countriesLabel": "Systems Deployed",
        "stats.stack": "Full-Stack",
        "stats.stackLabel": "Web + Mobile + DevOps",
        "stats.company": "Founder",
        "stats.companyLabel": "Fabricantes Digital SpA",
        // Services
        "services.title": "What I Build",
        "services.subtitle": "I take ownership of the full product — from architecture to deployment.",
        "services.arch.title": "System Architecture",
        "services.arch.desc": "Backend systems designed to handle real load. Distributed services, robust APIs, and database schemas built to scale from day one.",
        "services.arch.from": "From $3,000",
        "services.mobile.title": "Web + Mobile Apps",
        "services.mobile.desc": "Cross-platform applications with a shared backend. React on web, React Native on iOS & Android — one team, two platforms, zero compromises.",
        "services.mobile.from": "From $5,000",
        "services.fintech.title": "Payment & Automation",
        "services.fintech.desc": "Cross-border payment integrations (dLocal, Belo, TransFi), KYC/KYB pipelines, and n8n + AI workflows that make your operation run autonomously.",
        "services.fintech.from": "From $2,000",
        // Skills
        "skills.title": "Architecture & Ecosystem",
        "skills.description": "7 years of production-grade experience across the full stack. From distributed Java backends handling thousands of transactions, to React Native apps deployed on both platforms, to infrastructure engineered for cost-efficiency in LatAm.",
        // Parallax
        "parallax.skills": "Architecture & Ecosystem",
        "parallax.portfolio": "Case Studies",
        // Portfolio
        "portfolio.title": "Featured Work",
        "portfolio.fabricantes.desc": "End-to-end B2B/B2C clothing marketplace in LatAm. Built the React web app, React Native mobile app, Spring Boot backend with Meilisearch (sub-50ms search), and dLocal/Belo payments. Multi-cloud VPS infra (Oracle/OVH) reduced AWS costs by 60%. Founder & Lead Engineer.",
        "portfolio.nemesio.desc": "Waste management platform for Bendito Residuo. Designed a Svelte frontend, Django RDF backend, and automated Jenkins deployment pipelines for corporate clients to track recycling KPIs and regulatory compliance.",
        "portfolio.swappi.desc": "Real-time condominium management application for iOS & Android. Handles common expenses, maintenance requests, and shared scheduling using a single React Native codebase.",
        "portfolio.sgd.desc": "Debt Management System for the Central Bank of Chile. Engineered a high-security, zero-downtime Java/Spring Boot backend and React frontend deployed on JBoss servers via Jenkins CI/CD.",
        "portfolio.transbank.desc": "DevOps engineering at Chile's leading payment processor. Scaled deployments via Infrastructure as Code (AWS & Terraform), Jenkins CI/CD, and Artifactory binary management.",
        "portfolio.visit": "Visit Project",
        "portfolio.contact": "Inquire about Project",
        // Process
        "process.title": "How We Work",
        "process.step1.title": "Discovery Call",
        "process.step1.desc": "Free · 30 min. I understand your actual problem, not just the symptoms. If I can't help you, I'll tell you who can.",
        "process.step2.title": "Technical Proposal",
        "process.step2.desc": "Within 48h: architecture document, full scope, and investment. No surprises, no fine print.",
        "process.step3.title": "Build & Iterate",
        "process.step3.desc": "Weekly sprints with direct access to me — no PM middleman. Your code from day one.",
        "process.step4.title": "Delivery & Handoff",
        "process.step4.desc": "Full documentation, technical training, and post-launch support included.",
        // Contact
        "contact.getIn": "Let's Work",
        "contact.touch": "Together",
        "contact.subtitle": "Tell me about your project. I respond within 24h to qualified opportunities.",
        "contact.name": "Your Name",
        "contact.email": "Your Email",

        "contact.message": "Describe your problem or project",
        "contact.submit": "Send My Request →",

        "contact.successTitle": "Request Received",
        "contact.successDesc": "I'll get back to you within 24 hours.",
        "contact.errorTitle": "Something went wrong",
        "contact.errorDesc": "Please try again or email me directly.",
        "contact.emailTitle": "Direct Email",
        "contact.copy": "Copy Email",
        "contact.copied": "Copied!",
        "contact.copiedDesc": "Email address copied to clipboard.",
    },
    es: {
        // Nav
        "nav.home": "Inicio",
        "nav.skills": "Stack",
        "nav.services": "Servicios",
        "nav.portfolio": "Proyectos",
        "nav.contact": "Contáctame",
        // Hero
        "home.hi": "Construyo Software que Mueve Dinero y Escala Negocios",
        "home.role": "Ingeniero de Producto Senior · Fintech & Marketplaces",
        "home.description": "7 años diseñando sistemas backend, aplicaciones multiplataforma y flujos de pago automatizados para banca, fintech y e-commerce. Fundador de Fabricantes Digital SpA. No solo escribo código — diseño soluciones que generan ingresos.",
        "home.letsTalk": "Hablemos de tu Proyecto →",
        "home.badge1": "Sistemas Bancarios & Fintech",
        "home.badge2": "Pagos Cross-border",
        "home.badge3": "Arquitectura de Marketplaces",
        // Stats
        "stats.years": "Años",
        "stats.yearsLabel": "en Banca & Fintech",
        "stats.countries": "Países",
        "stats.countriesLabel": "Sistemas Desplegados",
        "stats.stack": "Full-Stack",
        "stats.stackLabel": "Web + Móvil + DevOps",
        "stats.company": "Fundador",
        "stats.companyLabel": "Fabricantes Digital SpA",
        // Services
        "services.title": "Qué Construyo",
        "services.subtitle": "Me hago cargo del producto completo — desde la arquitectura hasta el despliegue.",
        "services.arch.title": "Arquitectura de Sistemas",
        "services.arch.desc": "Sistemas backend diseñados para soportar carga real. Servicios distribuidos, APIs robustas y esquemas de base de datos construidos para escalar desde el día uno.",
        "services.arch.from": "Desde $3,000",
        "services.mobile.title": "Apps Web + Móvil",
        "services.mobile.desc": "Aplicaciones multiplataforma con backend compartido. React en web, React Native en iOS & Android — un equipo, dos plataformas, cero compromisos.",
        "services.mobile.from": "Desde $5,000",
        "services.fintech.title": "Pagos & Automatización",
        "services.fintech.desc": "Integraciones de pago cross-border (dLocal, Belo, TransFi), pipelines KYC/KYB y flujos con n8n + IA que hacen que tu operación funcione de forma autónoma.",
        "services.fintech.from": "Desde $2,000",
        // Skills
        "skills.title": "Arquitectura & Ecosistema",
        "skills.description": "7 años de experiencia full-stack. Experto en backends Java de alto rendimiento, apps móviles multiplataforma e infraestructura optimizada en costos.",
        // Parallax
        "parallax.skills": "Arquitectura & Ecosistema",
        "parallax.portfolio": "Casos de Estudio",
        // Portfolio
        "portfolio.title": "Proyectos Destacados",
        "portfolio.fabricantes.desc": "Marketplace B2B/B2C de indumentaria. Desarrollé web en React, app en React Native, backend Spring Boot con Meilisearch (búsqueda <50ms) y pagos cross-border (dLocal/Belo). Infraestructura multi-cloud (Oracle/OVH) 60% más económica que AWS. Fundador e Ingeniero Principal.",
        "portfolio.nemesio.desc": "Plataforma de gestión de residuos industriales. Desarrollé frontend en Svelte y backend en Django para seguimiento de KPIs. Despliegue automatizado con pipelines Jenkins.",
        "portfolio.swappi.desc": "App móvil de gestión de condominios en tiempo real para iOS y Android. Gestiona gastos y mantenciones usando una base de código unificada en React Native.",
        "portfolio.sgd.desc": "Sistema de Gestión de Deuda para el Banco Central de Chile. Desarrollé backend de alta seguridad en Java/Spring Boot y frontend en React, desplegado en servidores JBoss mediante Jenkins.",
        "portfolio.transbank.desc": "Ingeniería DevOps en la mayor procesadora de pagos de Chile. Automatización con Infraestructura como Código (Terraform), Jenkins CI/CD y gestión de artefactos en Artifactory.",
        "portfolio.visit": "Visitar Proyecto",
        "portfolio.contact": "Consultar por este Proyecto",
        // Process
        "process.title": "Cómo Trabajamos",
        "process.step1.title": "Discovery Call",
        "process.step1.desc": "Gratis · 30 min. Entiendo tu problema real, no solo los síntomas. Si no puedo ayudarte, te digo quién puede.",
        "process.step2.title": "Propuesta Técnica",
        "process.step2.desc": "En 48h: documento de arquitectura, alcance completo e inversión. Sin sorpresas, sin letra chica.",
        "process.step3.title": "Construir & Iterar",
        "process.step3.desc": "Sprints semanales con acceso directo a mí — sin PM intermediario. Tu código desde el día uno.",
        "process.step4.title": "Entrega & Handoff",
        "process.step4.desc": "Documentación completa, capacitación técnica y soporte post-lanzamiento incluidos.",
        // Contact
        "contact.getIn": "Trabajemos",
        "contact.touch": "Juntos",
        "contact.subtitle": "Cuéntame tu proyecto. Respondo en 24h a oportunidades calificadas.",
        "contact.name": "Tu Nombre",
        "contact.email": "Tu Correo",

        "contact.message": "Describe tu problema o proyecto",
        "contact.submit": "Enviar Solicitud →",

        "contact.successTitle": "Solicitud Recibida",
        "contact.successDesc": "Te respondo dentro de las próximas 24 horas.",
        "contact.errorTitle": "Algo salió mal",
        "contact.errorDesc": "Por favor intenta de nuevo o escríbeme directamente.",
        "contact.emailTitle": "Correo Directo",
        "contact.copy": "Copiar Correo",
        "contact.copied": "¡Copiado!",
        "contact.copiedDesc": "El correo ha sido copiado al portapapeles.",
    }
};

interface LanguageContextType {
    language: Language;
    t: (key: string) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const LanguageProvider = ({ children }: { children: ReactNode }) => {
    const [language, setLanguage] = useState<Language>('en');

    useEffect(() => {
        const fetchCountryAndSetLanguage = async () => {
            try {
                // Fetch trace data from Cloudflare to get cf-ipcountry equivalent
                const res = await fetch('https://1.1.1.1/cdn-cgi/trace');
                const text = await res.text();
                const locMatch = text.match(/loc=(.*)/);
                if (locMatch && locMatch[1]) {
                    const country = locMatch[1].toUpperCase();
                    // Latin countries + Spain
                    const spanishCountries = ['AR', 'BO', 'CL', 'CO', 'CR', 'CU', 'DO', 'EC', 'SV', 'GT', 'HN', 'MX', 'NI', 'PA', 'PY', 'PE', 'PR', 'UY', 'VE', 'ES'];
                    if (spanishCountries.includes(country)) {
                        setLanguage('es');
                    } else {
                        setLanguage('en');
                    }
                }
            } catch (e) {
                console.error("Failed to detect country", e);
            }
        };

        fetchCountryAndSetLanguage();
    }, []);

    const t = (key: string): string => {
        return translations[language][key] || key;
    };

    return (
        <LanguageContext.Provider value={{ language, t }}>
            {children}
        </LanguageContext.Provider>
    );
};

export const useLanguage = () => {
    const context = useContext(LanguageContext);
    if (!context) {
        throw new Error('useLanguage must be used within a LanguageProvider');
    }
    return context;
};
